'use client';

import React from 'react';
import { useTranslation } from 'react-i18next';

import { BadgeProps } from '@sola/ark-core/atoms/badge/badge.js';
import { NumberField } from '@sola/ark-core/molecules/number-field/number-field.js';
import {
  RadioGroup,
  RadioItemProps,
} from '@sola/ark-core/molecules/radio-group/radio-group.js';
import { cn } from '@sola/ark-core/utils/helpers.js';
import { BadgeColors, FundOptionProps } from '@sola/schema/lib/schema.js';
import { camelCase } from '@sola/utils/string.js';

import { FundQueryParams } from '../../../../utils/query-params';
import { useDonate } from '../donate-context';
import { DonateActionEnum, DonateFrequency } from '../donate.types';
import { useDonateActions } from '../hooks/use-donate-actions';
import { useDonateValidation } from '../hooks/use-donate-validation';

export const DonateAmountSelector: React.FC<{
  className?: string;
  focusOnLoad?: boolean;
  alt?: boolean;
}> = ({ className, focusOnLoad = false, alt }) => {
  const { t } = useTranslation();

  const { config, state, props, variant, fundParams, refs, dispatch } =
    useDonate();
  const { getCurrentAmount } = useDonateValidation();

  const {
    handleAmountSelect,
    handleOtherAmountChange,
    handleInputFocus,
    handleInputBlur,
  } = useDonateActions();

  const getRadioItemsForFrequency = (
    frequency: DonateFrequency,
  ): RadioItemProps[] => {
    const freq = camelCase(frequency) as keyof FundQueryParams;
    const defaultFreq = `default${frequency}` as keyof FundQueryParams;

    const suggestedAmounts =
      (fundParams?.[freq] as number[])
        ?.filter(
          (v) =>
            v >= config.donationLimits.min && v <= config.donationLimits.max,
        )
        ?.map((v) => {
          return {
            value: v,
            isDefault: v === fundParams?.[defaultFreq],
          } as FundOptionProps;
        }) ??
      props.fund?.[`suggestedAmounts${frequency}`] ??
      [];

    const items: RadioItemProps[] = [];
    if (suggestedAmounts.length >= 2) {
      suggestedAmounts.forEach((amount) => {
        items.push({
          value: amount.value.toString(),
          text: t('numbers.currencyCustom', { amount: amount.value }),
          subText:
            variant === 'grid' && amount.label ? amount.label : undefined,
          badge: amount.badge
            ? ({
                label: amount.badge,
                color: 'positive' as BadgeColors,
              } as BadgeProps)
            : undefined,
          type: 'default',
        });
      });
    }
    items.push({
      value: 'other',
      type: 'other',
      text: t('skippy.donation.other'),
      element: (
        <NumberField
          key={`${frequency}-other`}
          inputRef={refs[frequency]}
          name={frequency}
          aria-label={t('skippy.donation.other')}
          placeholder="0"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            handleOtherAmountChange(e.target.value)
          }
          value={state.otherAmounts[frequency]}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          prefix="$"
        />
      ),
    });

    return items;
  };

  const items = getRadioItemsForFrequency(state.donateFrequency);

  if (!items.length) {
    return null;
  }

  const suggestedCount =
    (
      fundParams?.[
        camelCase(state.donateFrequency) as keyof FundQueryParams
      ] as number[]
    )?.length ||
    props.fund?.[`suggestedAmounts${state.donateFrequency}`]?.length ||
    0;
  const suggestedAmountOnly =
    config.enabledFrequencies.length === 0 || suggestedCount === 0;

  return (
    <div
      className={`DonateAmountSelector ${alt ? 'flex-1' : ''} ${className || ''}`}
    >
      {alt && (
        <div className="Donate__other-input">
          <NumberField
            name="multiple-other"
            label={t('skippy.donation.amount')}
            placeholder="0"
            value={
              state.otherAmounts[state.donateFrequency] ||
              props?.fund?.suggestedAmount
            }
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              state.otherAmounts.Monthly = e.target.value;
              state.otherAmounts.OneTime = e.target.value;
              dispatch({
                type: DonateActionEnum.SET_OTHER_AMOUNT,
                payload: {
                  frequency: state.donateFrequency,
                  amount: e.target.value,
                },
              });
            }}
            prefix="$"
            isFocused={focusOnLoad}
          />
        </div>
      )}

      {!alt && !suggestedAmountOnly && (
        <div
          className={cn(`Donate__items-${suggestedCount}`, {
            'Donate__other-input': state.otherSelected,
          })}
        >
          <RadioGroup
            key={`${state.donateFrequency}-amounts`}
            name={`${state.donateFrequency}-donateAmount`}
            aria-label={t('skippy.donation.chooseAmount')}
            items={items}
            variant={variant === 'grid' ? 'grid' : 'buttons'}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleAmountSelect(e.target.value);
            }}
            value={state.selectedAmounts[state.donateFrequency].toString()}
          />
        </div>
      )}
      {!alt && suggestedAmountOnly && (
        <div className="Donate__other-suggestedAmount">
          <p className="label-potent pb-snug-xl">
            {props?.fund?.suggestedAmount &&
              t('skippy.donation.suggestedGift', {
                amount: props?.fund?.suggestedAmount,
              })}
          </p>
          <NumberField
            key={`${state.donateFrequency}-other`}
            inputRef={refs[state.donateFrequency]}
            name={state.donateFrequency}
            aria-label={t('skippy.donation.other')}
            placeholder="0"
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleOtherAmountChange(e.target.value)
            }
            value={state.otherAmounts[state.donateFrequency]}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            prefix="$"
            isFocused={focusOnLoad}
          />
        </div>
      )}
    </div>
  );
};
